import { useContext, useState } from 'react'
import { ConnectionStepProps } from 'pages/MarketingPage/Components/StepsSocialMedia/ConnectionStep'
import { AdConnectionType, PlanType } from 'modules/Const'
import { UpgradeModalContext } from 'Hooks/UpgradeModalContext'
import useFacebookConnections from 'Hooks/useFacebookConnections'
import useBrand from 'Hooks/useBrand'
import { useFeature } from 'Hooks/useFeature'
import { FeatureKeys } from 'features/definitions'
import Icon from 'elements/Icon'
import millify from 'millify'
import { CurrentBrand } from 'modules/Types';
import { CURRENCY_CODE_DATA } from 'helpers/currencies'
import Colors from 'modules/Colors'

interface UseFacebookConnectionStepsOptions {
  /** Context for where the connections are being used (affects upgrade modal source) */
  context: 'campaign' | 'settings'
  /** Callback when a connection type is selected */
  onConnectionTypeSelected: (type: AdConnectionType) => void;
  /** Whether to show the pixel connection step */
  includePixelStep?: boolean;
  /** Custom upgrade call to action for pixel connection */
  pixelUpgradeCallToAction?: string;
  /** Custom className for connection icons */
  iconClassName?: string;
  /** Callback to open the Conversions API Token Modal */
  onOpenConversionsApiModal?: () => void; // Added for the new step
}

interface UseFacebookConnectionStepsReturn {
  connectionSteps: ConnectionStepProps[];
  handlePixelConnectionClick: (type: AdConnectionType.FACEBOOK_PIXEL | AdConnectionType.FACEBOOK_CONVERSIONS_API_TOKEN) => void;
  /** State for showing the logout confirmation modal */
  showLogoutModal: boolean
  /** Handler to show the logout confirmation modal */
  handleShowLogoutModal: () => void
  /** Handler to cancel logout and close modal */
  handleCancelLogout: () => void
  /** Handler to confirm logout */
  handleConfirmLogout: () => void
  /** Whether logout is in progress */
  isLoggingOut: boolean;
}

const useFacebookConnectionSteps = ({
  context,
  onConnectionTypeSelected,
  includePixelStep = true,
  pixelUpgradeCallToAction,
  iconClassName = 'connectionStepIcon',
  onOpenConversionsApiModal // Added for the new step
}: UseFacebookConnectionStepsOptions): UseFacebookConnectionStepsReturn => {
  const { handleOpenModal } = useContext(UpgradeModalContext)
  const pixelFeature = useFeature(FeatureKeys.CAMPAIGN_FACEBOOK_PIXEL)

  // Add state for logout confirmation modal
  const [showLogoutModal, setShowLogoutModal] = useState(false)
  const [isLoggingOut, setIsLoggingOut] = useState(false)

  const {
    loggedInFacebookUser,
    isLoggingIntoFacebook,
    loginWithFacebook,
    selectedFacebookAdAccount,
    selectedFacebookPixel,
    selectedFacebookPage,
    selectedInstagramPage,
    pixelConversionsAccessToken,
    logoutFacebook
  } = useFacebookConnections();

  /** Handler to show the logout confirmation modal */
  const handleShowLogoutModal = () => {
    setShowLogoutModal(true)
  }

  /** Handler to cancel logout and close modal */
  const handleCancelLogout = () => {
    setShowLogoutModal(false)
  }

  /** Handler to confirm logout */
  const handleConfirmLogout = async () => {
    try {
      setIsLoggingOut(true)
      await logoutFacebook()
      setShowLogoutModal(false)
    } catch (error) {
      console.error('Error logging out of Facebook:', error)
    } finally {
      setIsLoggingOut(false)
    }
  }

  /**
   * Handles pixel connection click - checks feature availability first
   * Shows upgrade UI for users without access, opens connection modal for users with access
   */
  const handlePixelConnectionClick = (type: AdConnectionType.FACEBOOK_PIXEL | AdConnectionType.FACEBOOK_CONVERSIONS_API_TOKEN) => {
    if (!pixelFeature.isAvailable) {
      const source = context === 'campaign' 
        ? 'Campaign Creation - Facebook Pixel Connection'
        : 'Settings - Facebook Pixel Connection'
      
      const defaultCallToAction = context === 'campaign'
        ? 'Unlock Facebook Pixel tracking and advanced conversions with Symphony Growth'
        : 'Link your Facebook Pixel and enable conversions with Symphony Growth'

      handleOpenModal({
        source,
        highlightedPlan: pixelFeature.minRequiredPlan || PlanType.PRO,
        onlyShowPlans: pixelFeature.availablePlans as PlanType[],
        upgradeCallToAction: pixelUpgradeCallToAction || defaultCallToAction
      })
      return
    }
    if(type === AdConnectionType.FACEBOOK_CONVERSIONS_API_TOKEN) {
      if (onOpenConversionsApiModal) {
        onOpenConversionsApiModal();
      }
      return
    } else {
      onConnectionTypeSelected(type)
    }
  }

  const buildConnectionSteps = (): ConnectionStepProps[] => {
    const selectedAdAccountCurrency = selectedFacebookAdAccount?.currency || 'USD';
    const selectedAdAccountCurrencyName = CURRENCY_CODE_DATA[selectedAdAccountCurrency]?.name_plural || 'United States Dollar';
    const conversionsApiAccessToken = pixelConversionsAccessToken;

    let steps: ConnectionStepProps[] = [
      {
        stepTitle: 'Facebook Profile',
        connectedAccountName: loggedInFacebookUser?.name || 'Facebook Profile',
        connectedAccountIcon: (
          <Icon
            className={iconClassName}
            icon={loggedInFacebookUser?.picture?.data.url || 'facebook'}
            iconType={loggedInFacebookUser ? 'custom' : 'default'}
            size={40}
          />
        ),
        stepDescription: 'Login with your Facebook profile to connect your advertising accounts and pages.',
        isCompleted: Boolean(loggedInFacebookUser),
        isLoading: isLoggingIntoFacebook,
        onButtonClick: loggedInFacebookUser ? handleShowLogoutModal : loginWithFacebook,
        canEdit: Boolean(loggedInFacebookUser),
        buttonText: loggedInFacebookUser ? 'Log out' : undefined
      },
      {
        stepTitle: 'Ad Account',
        connectedAccountName: selectedFacebookAdAccount?.name || 'Connect your Ad Account',
        connectedAccountIcon: (
          <Icon
            className={iconClassName}
            icon="facebook"
            size={40}
          />
        ),
        stepDescription: 'Your Facebook ad account where Symphony will publish your ads. You\'ll be billed based on your ad account\'s billing method.',
        isCompleted: Boolean(selectedFacebookAdAccount),
        onButtonClick: () => {
          onConnectionTypeSelected(AdConnectionType.FACEBOOK_AD_ACCOUNT)
        },
        connectedAccountDescriptions: selectedFacebookAdAccount ? [
          `$${millify(selectedFacebookAdAccount?.amount_spent || 0)} spent`,
          context === 'campaign'
            ? `This ad account uses the currency ${selectedAdAccountCurrencyName} (${selectedAdAccountCurrency}).`
            : `Uses ${selectedAdAccountCurrencyName} (${selectedAdAccountCurrency})`
        ] : undefined,
        canEdit: true,
      },
      {
        stepTitle: context === 'campaign' ? 'Facebook + Instagram Pages' : 'Facebook Page',
        connectedAccountName: selectedFacebookPage?.name || 'Connect your Facebook Page',
        connectedAccountIcon: (
          <Icon
            className={iconClassName}
            icon={selectedFacebookPage?.picture?.data.url || 'facebook'}
            iconType={selectedFacebookPage ? 'custom' : 'default'}
            size={40}
          />
        ),
        stepDescription: 'Select the Facebook Page and Instagram Page you want to use for your ads.',
        isCompleted: Boolean(selectedFacebookPage),
        onButtonClick: () => {
          onConnectionTypeSelected(AdConnectionType.FACEBOOK_PAGE)
        },
        canEdit: true,
        iconContainerColor: Colors.grey,
        isLastStep: !selectedFacebookPage && !includePixelStep && !conversionsApiAccessToken
      }
    ];

    // Add Instagram page step if Facebook page is connected
    if (selectedFacebookPage) {
      steps.push({
        stepTitle: 'Instagram Page',
        connectedAccountName: selectedInstagramPage?.instagramAccount?.username || 'Connect your Instagram Page',
        connectedAccountIcon: (
          <Icon
            className={iconClassName}
            icon={selectedInstagramPage?.instagramAccount?.profile_picture_url || 'instagramFlat'}
            iconType={selectedInstagramPage ? 'custom' : 'default'}
            size={40}
          />
        ),
        stepDescription: 'This Instagram page will be the one seen by Instagram ad viewers. Your Instagram page needs to be connected to the Facebook page you\'ve linked above.',
        isCompleted: Boolean(selectedInstagramPage),
        onButtonClick: () => {
          onConnectionTypeSelected(AdConnectionType.FACEBOOK_PAGE)
        },
        canEdit: true,
        isLastStep: !includePixelStep && !conversionsApiAccessToken
      });
    }

    // Determine if the pixel step is the last one
    const isPixelStepLast = !conversionsApiAccessToken;

    // Add pixel connection step if requested
    if (includePixelStep) {
      steps.push({
        stepTitle: context === 'campaign' ? 'Enable Conversions for immediate impact' : 'Facebook Pixel (Ad Campaigns)',
        connectedAccountName: selectedFacebookPixel?.name ?? selectedFacebookPixel?.id ?? 'Connect your Facebook Pixel',
        connectedAccountIcon: (
          <Icon
            className={iconClassName}
            icon="facebook"
            iconType="default"
            size={40}
          />
        ),
        stepDescription: pixelFeature.isAvailable
          ? '(Optional) Improve your ad performance by adding your Facebook Pixel for better results.'
          : '(Growth Feature) Unlock advanced conversion tracking by upgrading to Symphony Pro.',
        isCompleted: Boolean(selectedFacebookPixel && selectedFacebookPixel?.id),
        onButtonClick: () => handlePixelConnectionClick(AdConnectionType.FACEBOOK_PIXEL),
        canEdit: true,
        iconContainerColor: pixelFeature.isAvailable ? Colors.grey : Colors.purpleLighter,
        isLastStep: true
      })
    }

    // Add Conversions API Access Token step
    // This step should appear regardless of includePixelStep, but its isLastStep depends on includePixelStep
    const hasConversionsToken = Boolean(conversionsApiAccessToken);
    let conversionsApiStepDescription = '(Optional) Link your Conversions API access token to track events on the server, and get around ad blockers';

    steps.push({
      stepTitle: '(Advanced) Conversions API Access Token',
      connectedAccountName: hasConversionsToken ? 'Your token is linked!' : 'Link your Conversions API Token',
      connectedAccountIcon: (
        <Icon
          className={iconClassName}
          icon="facebook" // You might want a different icon here
          iconType="default"
          size={40}
        />
      ),
      stepDescription: conversionsApiStepDescription,
      isCompleted: hasConversionsToken,
      onButtonClick: () => handlePixelConnectionClick(AdConnectionType.FACEBOOK_CONVERSIONS_API_TOKEN),
      canEdit: true,
      iconContainerColor: Colors.grey, // Or another color if preferred
      isLastStep: true // This will now always be the last step
    });

    // Adjust isLastStep for previous steps if CAPI is not the last one added
    // This logic is a bit tricky. If includePixelStep is false, and CAPI is added, then IG or FB page might be last.
    // The simplest is to ensure CAPI is always added last and is marked as isLastStep: true.
    // Then, the step before CAPI should have its isLastStep set to false.
    if (steps.length > 1) {
      for (let i = 0; i < steps.length - 1; i++) {
        steps[i].isLastStep = false;
      }
    }


    return steps;
  };

  return {
    connectionSteps: buildConnectionSteps(),
    handlePixelConnectionClick,
    showLogoutModal,
    handleShowLogoutModal,
    handleCancelLogout,
    handleConfirmLogout,
    isLoggingOut
  };
};

export default useFacebookConnectionSteps;